<div class="image-refiner-container">
  <div class="header">
    <h2>{{ 'ImageGenerator.RefineTitle' | translate }}</h2>
    <button mat-icon-button (click)="close.emit()">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  @if (isMobile()) {
    <mat-tab-group class="refiner-tab-group">
      <mat-tab [label]="'ImageGenerator.RefineButton' | translate">
        <div class="mobile-content-area custom-scrollbar">
          <ng-container *ngTemplateOutlet="refineTemplate"></ng-container>
        </div>
      </mat-tab>
      <mat-tab [label]="'ImageGenerator.Versions' | translate">
        <div class="mobile-content-area custom-scrollbar">
          <ng-container *ngTemplateOutlet="historyTemplate"></ng-container>
        </div>
      </mat-tab>
    </mat-tab-group>
  } @else {
    <div class="content">
      <ng-container *ngTemplateOutlet="historyTemplate"></ng-container>
    </div>

    <ng-container *ngTemplateOutlet="refineTemplate"></ng-container>
  }
</div>

<ng-template #historyTemplate>
  <div class="history-tab-content">
    <!-- UPPER PART: Preview -->
    @if (currentVersion(); as version) {
      <div class="preview-section">
        <div class="image-preview">
          @if (currentSecureImageUrl()) {
            <app-image-viewer [src]="currentSecureImageUrl()" [alt]="version.prompt" objectFit="contain" borderRadius="12px" [fillContainer]="true"></app-image-viewer>
          } @else {
            <div class="spinner-dots">
              <mat-spinner diameter="24"></mat-spinner>
            </div>
          }
          <div class="image-prompt-overlay">
            <p>{{ version.prompt }}</p>
          </div>
        </div>
      </div>
    }

    <!-- MIDDLE PART: History List -->
    <div class="history-section">
      <h3>{{ 'ImageGenerator.Versions' | translate }}</h3>

      <div class="versions-list">
        @for (ver of reversedVersions(); track ver._id) {
          <div class="version-item" [class.active]="selectedVersionId() === ver._id" (click)="selectVersion(ver._id)">
            <div class="version-info">
              <span class="version-label">{{ ver.instruction || ver.prompt }}</span>
              <span class="version-date">{{ ver.createdAt | date: 'dd/MM/yyyy HH:mm' }}</span>
            </div>

            <div class="version-actions">
              @if (selectedVersionId() === ver._id) {
                <button mat-icon-button class="insert-btn-tile" matTooltip="{{ 'ImageGenerator.InsertImage' | translate }}" (click)="insertImage($event)">
                  <mat-icon>arrow_insert</mat-icon>
                </button>
                <mat-icon class="check-icon">check_circle</mat-icon>
              } @else {
                <button mat-icon-button class="delete-btn" (click)="deleteVersion($event, ver._id)">
                  <mat-icon>delete</mat-icon>
                </button>
              }
            </div>
          </div>
        }
      </div>
    </div>
  </div>
</ng-template>

<ng-template #refineTemplate>
  <div [class.refiner-footer]="!isMobile()" [class.refiner-footer-content]="isMobile()">
    <form [formGroup]="regenerateForm" (ngSubmit)="regenerate()">
      <div class="instruction-area">
        <label class="instruction-label">{{ 'ImageGenerator.Refine' | translate }}</label>
        <textarea formControlName="instruction" rows="2" [placeholder]="'ImageGenerator.RefinePlaceholder' | translate" (keydown.enter)="$event.preventDefault(); regenerate()"></textarea>
      </div>

      <div class="options-row">
        <div class="option-field">
          <label class="option-label">{{ 'ImageGenerator.AspectRatioLabel' | translate }}</label>
          <mat-select formControlName="aspectRatio">
            @for (opt of aspectRatios; track opt.value) {
              <mat-option [value]="opt.value">{{ opt.label | translate }}</mat-option>
            }
          </mat-select>
        </div>

        <div class="option-field">
          <label class="option-label">{{ 'ImageGenerator.ModelLabel' | translate }}</label>
          <mat-select formControlName="model">
            @for (m of models; track m.value) {
              <mat-option [value]="m.value">{{ m.label | translate }}</mat-option>
            }
          </mat-select>
        </div>
      </div>

      <!-- Media Context Indicator -->
      @if (attachedFiles() && attachedFiles().length > 0) {
        <div class="attached-files-container mt-3">
          @for (file of attachedFiles(); track file.id) {
            <!-- Regular File Tile -->
            <div class="media-context-card mb-2 flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div class="flex items-center gap-3">
                  <div class="icon-wrapper flex items-center justify-center w-10 h-10 rounded-lg bg-indigo-50 text-indigo-500">
                    @if (file.uploading) {
                      <mat-spinner diameter="20"></mat-spinner>
                    } @else {
                      @if (file.type === 'video') {
                        <mat-icon class="media-icon">videocam</mat-icon>
                      } @else if (file.type === 'image') {
                        <mat-icon class="media-icon">image</mat-icon>
                      } @else {
                        <mat-icon class="media-icon">description</mat-icon>
                      }
                    }
                  </div>
                  <div class="info-wrapper flex flex-col">
                    <h4 [matTooltip]="file.name" class="m-0 text-sm font-medium max-w-[200px] truncate">{{ file.name }}</h4>
                    @if (file.uploading) {
                      <p class="m-0 text-xs text-gray-500">{{ 'FileUploader.Uploading' | translate }}...</p>
                    } @else {
                      <p class="m-0 text-xs text-gray-500">{{ 'ContentGenerator.FileAttached' | translate }}</p>
                    }
                  </div>
                </div>
              <button type="button" mat-icon-button class="remove-btn" (click)="removeAttachment(file.id)">
                <mat-icon>close</mat-icon>
              </button>
            </div>
          }
        </div>
      }

      <div class="file-actions mt-3 flex gap-2 flex-wrap mb-2">
        <button type="button" mat-stroked-button (click)="fileInput.click()">
          <mat-icon>attach_file</mat-icon>
          {{ 'ContentGenerator.AttachFile' | translate }}
        </button>
        <input type="file" #fileInput hidden (change)="onFileSelected($event)" accept="image/*" />
      </div>

      <button mat-flat-button color="primary" class="refine-btn" type="submit" [disabled]="regenerateForm.invalid || isRegenerating() || !allFilesDoneUploading()">
        <div class="btn-content">
          @if (isRegenerating()) {
            <mat-spinner diameter="20"></mat-spinner>
          } @else {
            <mat-icon>auto_fix_high</mat-icon>
          }
          <span>{{ 'ImageGenerator.RefineButton' | translate }}</span>
        </div>
      </button>
    </form>
  </div>
</ng-template>
